import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { get } from 'redux/centers/actions'
import { SelectWithLabel } from '..'
import './styles.scss'

const styles = {
  multiValue: (base, state) => {
    return state.data.isFixed ? { ...base, backgroundColor: '#b9b9b9 !important', paddingRight: '10px !important' } : base
  },
  multiValueLabel: (base, state) => {
    return state.data.isFixed ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 } : base
  },
  multiValueRemove: (base, state) => {
    return state.data.isFixed ? { ...base, display: 'none' } : base
  },
}

function getObjectValues (objValues, selectedValues) {
  return Object.values(objValues).map((item) => ({
    value: item._id,
    label: item.name,
    data: item,
    disabled: item._id === 'all' ? false : selectedValues && selectedValues.some(v => v.value === 'all')
  }))
}

function getRightName(text) {
  if (['all', 'some'].includes(text)) return I18n.t(`dashboard_${text}-plural-female`)

  return text
}

function orderItems (external, asigned) {
  let result = external
    .reduce((preV, next) => {
      const item = asigned.find(el => el._id === next.value)
      preV.push({
        value: item ? item._id : next._id || next.value,
        label: item ? item.name : getRightName(next.name) || next.label,
        data: item ? (item.data || item) : next.data || next || {},
        isFixed: true,
      })
      return preV
    }, [])
    .sort((a, b) => (a.isFixed === b.isFixed ? 0 : a.isFixed ? -1 : 1))
    .sort((a, b) => a.label-b.label)


  result.forEach(re => {
    asigned.forEach(as => {
      if (re.value === as._id) {
        re.isFixed = false
      }
    })
  })

  //REMOVE ITEMS IF ALL ES SELECTED
  if (result.some(it => it.value === 'all')) {
    return result.filter(it => it.value === 'all')
  }
  return result
}

function displayAllTag(role, optionsWithoutAll) {
  if (['admin', 'root', 'owner'].includes(role)) {
    if (!optionsWithoutAll) return  [{ _id:'all', name: I18n.t('dashboard_all-plural-female'), brand: 'all' }]
  }

  return []
}

function getDelegationOptions(delegations, selectedDelegations, brandsSelected = []) {
  if (!brandsSelected.length) return []
  return getObjectValues(delegations, selectedDelegations)
    .filter((elem) => [{ value: 'all' }].concat(brandsSelected).find((b) => b.value === elem.data.brand))
}

/**
 * Select brand and delegations with fixed options in select
 * @param {object} props
 * @param {[{_id: string, name: string}]} props.currentBrands
 * @param {[{_id: string, name: string, brand: string}]} props.currentDelegations
 * @param {function} props.onChangeBrands
 * @param {function} props.onChangeDelegations
 * @param {boolean} props.grouped
 */
const BrandsAndDelegations = (props) => {
  const {
    currentBrands = [],
    currentDelegations = [],
    grouped = true,
    onChangeBrands,
    onChangeDelegations,
    multi = true,
    labelInside,
    withLabel = true,
    displayAllValues = true,
    isClearable,
    newBrands = [],
    newDelegations = [],
    optionsWithoutAll,
    defaultPlaceholder,
    placeholderBrands,
    placeholderDelegations,
    classNameWrapper,
    required,
    otherTooltipProps,
    fullWidth = false
  } = props

  const dispatch = useDispatch()

  const centers = useSelector((store) => (store.centers))
  const user = useSelector((store) => (store.user))
  const [brands, setBrands] = React.useState(centers.brands || [])
  const [delegations, setDelegations] = React.useState(centers.delegations || [])
  const [brandOptions, setBrandOptions] = React.useState(getObjectValues(brands) || [])
  const [selectedBrands, setSelectedBrands] = React.useState(orderItems(currentBrands || [], brands))
  const [delegationsOptions, setDelegationsOptions] = React.useState(selectedBrands.length > 0 ? getObjectValues(delegations).filter((elem) => selectedBrands.find((b) => b.value === elem.data.brand)) : [])
  const [selectedDelegations, setSelectedDelegations] = React.useState(currentBrands.length > 0 ? orderItems(currentDelegations || [], delegations) : [])

  React.useEffect(()=>{
    if (centers?.isPending) {
      dispatch(get())
    } else {
      newBrands.length === 0 && setBrands((displayAllValues ? [...displayAllTag(user.role, optionsWithoutAll)] : []).concat(centers.brands))
      newDelegations.length === 0 && setDelegations((displayAllValues ? [...displayAllTag(user.role, optionsWithoutAll)] : []).concat(centers.delegations))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centers])

  React.useEffect(()=>{
    newBrands.length > 0 && setBrands((displayAllValues ? [...displayAllTag(user.role, optionsWithoutAll)] : []).concat(newBrands))
    newDelegations.length > 0 && setDelegations((displayAllValues ? [...displayAllTag(user.role, optionsWithoutAll)] : []).concat(newDelegations))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBrands, newDelegations])

  React.useEffect(()=>{
    const b = orderItems(currentBrands || [], brands)
    setSelectedBrands(b)
    if (b.some(it=>it.value==='all') && currentDelegations.some(it=>it.value!=='all')) {
      setSelectedDelegations([])
      onChangeDelegations([])
    }
    props.setRefBrandsDelegations && props.setRefBrandsDelegations({ brands: b, delegations: selectedDelegations })

    setBrandOptions(getObjectValues(brands, b) || [])
  }, [brands, currentBrands])

  React.useEffect(()=>{
    const d = currentBrands.length > 0 ? orderItems(currentDelegations || [], delegations) : []
    setSelectedDelegations(d)
    props.setRefBrandsDelegations && props.setRefBrandsDelegations({ brands: selectedBrands, delegations: d })
    setDelegationsOptions(getDelegationOptions(delegations, d, selectedBrands))

    if (currentBrands.length === 0 && currentDelegations.length > 0)
      onChangeDelegations([])
  }, [selectedBrands, delegations, currentDelegations])

  const onSelected = React.useCallback(
    (type, value, action, removedValue, option, onChange) => {
      switch (action) {
        case 'remove-value':
        case 'pop-value': {
          if (removedValue.isFixed) return
          break
        }
        case 'clear': {
          value = (type === 'brand' ? selectedBrands : selectedDelegations).filter((v) => v.isFixed)
          break
        }
        default:
          break
      }

      if (type === 'brands') {
        onChange(value || [])
        const _selectedBrands = value || []
        const _selectedDelegations = selectedDelegations.filter((d) => _selectedBrands.some(value => value.value === (d.data.brand)))
        onChangeDelegations(_selectedDelegations || [])
      }
      else if (type === 'delegations') {
        if (action === 'select-option' && (option.value === 'all' || (option.value !== 'all' && (value || []).some(it => it.value !== 'all'))))
          onChange(value || [])
        else if (action !== 'select-option')
          onChange(value || [])

        if ((!action === 'select-option' && !option.value === 'all')
          || (action === 'remove-value')) {
          const _selectedDelegations = value || []
          const _selectedBrands = selectedBrands.filter((d) => _selectedDelegations.some(value => (value.data.brand || value.data.data.brand) === d.value))
          onChangeBrands(_selectedBrands || [])
        }
      }
    },
    [selectedBrands, selectedDelegations]
  )

  return (
    <>
      {grouped &&
      <div className={`wrapper-mode-1 ${fullWidth && 'full-width'}`}>
        <div className='seccion-mode-1'>
          {!labelInside && withLabel && <label htmlFor={'brands'}>{I18n.t('dashboard_brands')}</label>}
          <SelectWithLabel
            name='brands'
            key='brands'
            label={labelInside && I18n.t('dashboard_brands')}
            options={brandOptions}
            multi={multi}
            value={selectedBrands}
            selectedValues={selectedBrands || currentBrands}
            onChange={(value)=>onChangeBrands(value || [])}
            placeholder={defaultPlaceholder
              ? I18n.t('dashboard_select')
              : placeholderBrands
                ? placeholderBrands
                : I18n.t('dashboard_selector_filter-by-brand')
            }
            classNameWrapper={`brand-delegation brands ${classNameWrapper ? classNameWrapper : ''}`}
            isOptionDisabled={(option) => {return option.disabled === true}}
            isClearable={isClearable}
            required={required}
            otherProps={otherTooltipProps}
            fullWidth={fullWidth}
          />
        </div>
        <div className='seccion-mode-1'>
          {!labelInside && withLabel && <label htmlFor={'brands'}>{I18n.t('dashboard_delegations')}</label>}
          <SelectWithLabel
            name='delegations'
            key='delegations'
            label={labelInside && I18n.t('dashboard_delegations')}
            options={delegationsOptions}
            multi={multi}
            value={selectedDelegations}
            selectedValues={selectedDelegations || currentDelegations}
            onChange={(value)=>onChangeDelegations(value || [])}
            placeholder={defaultPlaceholder
              ? I18n.t('dashboard_select')
              : placeholderDelegations
                ? placeholderDelegations
                : I18n.t('dashboard_selector_filter-by-delegation')
            }
            classNameWrapper={`brand-delegation delegations ${classNameWrapper ? classNameWrapper : ''}`}
            isClearable={isClearable}
            required={required}
            otherProps={otherTooltipProps}
            fullWidth={fullWidth}
          />
        </div>
      </div>
      }
      {!grouped &&
      <div className={`wrapper-mode-2 ${fullWidth && 'full-width'}`}>
        <div className='seccion-mode-2'>
          {!labelInside && <label htmlFor={'brands'}>{I18n.t('dashboard_brands')}</label>}
          <SelectWithLabel
            name='brands'
            key='brands'
            label={labelInside && I18n.t('dashboard_brands')}
            options={brandOptions}
            multi={multi}
            value={selectedBrands}
            selectedValues={selectedBrands}
            onChange={(value, { action, removedValue, option }) => onSelected('brands', value, action, removedValue, option, onChangeBrands)}
            placeholder={defaultPlaceholder
              ? I18n.t('dashboard_select')
              : placeholderBrands
                ? placeholderBrands
                : I18n.t('dashboard_selector_filter-by-brand')
            }
            classNameWrapper={`brand-delegation brands ${classNameWrapper ? classNameWrapper : ''}`}
            grouped={grouped}
            hideSelectedOptions={true}
            isClearable={selectedBrands.some((v) => !v.isFixed)}
            styles={styles}
            required={required}
            otherProps={otherTooltipProps}
          />
        </div>
        <div className='seccion-mode-2'>
          {!labelInside && <label htmlFor={'brands'}>{I18n.t('dashboard_delegations')}</label>}
          <SelectWithLabel
            name='delegations'
            key='delegations'
            label={labelInside && I18n.t('dashboard_delegations')}
            options={delegationsOptions}
            multi={multi}
            value={selectedDelegations}
            selectedValues={selectedDelegations}
            onChange={(value, { action, removedValue, option }) => onSelected('delegations', value, action, removedValue, option, onChangeDelegations)}
            placeholder={defaultPlaceholder
              ? I18n.t('dashboard_select')
              : placeholderDelegations
                ? placeholderDelegations
                : I18n.t('dashboard_selector_filter-by-delegation')
            }
            classNameWrapper={`brand-delegation delegations ${classNameWrapper ? classNameWrapper : ''}`}
            grouped={grouped}
            hideSelectedOptions={true}
            isClearable={selectedDelegations.some((v) => !v.isFixed)}
            styles={styles}
            required={required}
            otherProps={otherTooltipProps}
          />
        </div>
      </div>
      }
    </>
  )
}

export default BrandsAndDelegations
